import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { PrefetchNavLink } from '../ui/PrefetchLink';
import { NavIcon, NavIconName } from '../ui/NavIcon';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { useAuth } from '../../hooks/useAuth';
import { featureFlags } from '../../config/featureFlags';
import { ROUTES } from '../../constants';

const Container = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  width: 15rem;
  height: 100vh;
  background: var(--surface);
  border-right: 1px solid var(--border);
  z-index: var(--z-fixed);
  display: flex;
  flex-direction: column;
  @media (max-width: 767px) {
    position: relative;
    width: 100%;
    height: auto;
    border-right: 0;
    border-bottom: 1px solid var(--border);
  }
`;
const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.25rem;
  @media (max-width: 767px) {
    padding: 0.75rem 1rem;
  }
`;
const Brand = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.035em;
  color: var(--text-primary);
  span {
    display: block;
    margin-top: 0.125rem;
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: 0;
    color: var(--text-muted);
  }
`;
const MenuButton = styled(Button)`
  display: none;
  @media (max-width: 767px) {
    display: inline-flex;
  }
`;
const Navigation = styled.nav<{ $open: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 0.75rem 1rem;
  @media (max-width: 767px) {
    display: ${({ $open }) => ($open ? 'block' : 'none')};
    max-height: 70vh;
  }
`;
const SectionTitle = styled.p`
  margin: 1.5rem 0.75rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
`;
const NavItem = styled(PrefetchNavLink)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  min-height: 42px;
  border-radius: var(--radius-md);
  margin-bottom: 0.125rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 400;
  text-decoration: none;
  &:hover {
    background: var(--interactive-hover);
    transform: none;
  }
  &.active {
    background: var(--brand-50);
    color: var(--brand-700);
    font-weight: 600;
  }
  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: -2px;
  }
  @media (max-width: 767px) {
    min-height: 44px;
  }
`;
const Footer = styled.div`
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
`;
interface Destination {
  to: string;
  label: string;
  icon: NavIconName;
}
const dataLinks: Destination[] = [
  { to: '/data-entry', label: 'Añadir datos', icon: 'upload' },
  { to: '/transactions', label: 'Actividad', icon: 'invoices' },
  { to: ROUTES.USER_FILES, label: 'Archivos', icon: 'archive' },
  ...(featureFlags.COMPLIANCE
    ? [
        {
          to: '/compliance',
          label: 'Cumplimiento',
          icon: 'compliance' as const,
        },
      ]
    : []),
];
const restaurantLinks: Destination[] = [
  { to: '/analytics', label: 'Análisis', icon: 'analytics' },
  { to: '/goals', label: 'Metas', icon: 'goals' },
  { to: '/suppliers', label: 'Proveedores', icon: 'suppliers' },
  { to: '/transparency', label: 'Transparencia', icon: 'info' },
  { to: '/profile', label: 'Perfil', icon: 'profile' },
];

export const Sidebar: React.FC<{ className?: string }> = ({ className }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const menuRef = useRef<HTMLButtonElement>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const locationName =
    [...dataLinks, ...restaurantLinks].find((link) =>
      pathname.startsWith(link.to),
    )?.label ?? 'Inicio';

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        menuRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [menuOpen]);

  const handleNavigation = () => {
    if (menuOpen) {
      setMenuOpen(false);
      requestAnimationFrame(() =>
        document.getElementById('main-content')?.focus(),
      );
    }
  };
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      toast.success('Sesión cerrada correctamente');
      navigate('/auth/login', { replace: true });
    } catch {
      toast.error('Error al cerrar sesión. Inténtalo de nuevo.');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };
  const renderLink = ({ to, label, icon }: Destination) => (
    <NavItem key={to} to={to} onClick={handleNavigation}>
      <NavIcon name={icon} aria-hidden />
      <span>{label}</span>
    </NavItem>
  );
  return (
    <>
      <Container className={className}>
        <BrandRow>
          <Brand>
            Foodprint<span>by Baseline</span>
          </Brand>
          <MenuButton
            ref={menuRef}
            variant="tertiary"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            onClick={() => setMenuOpen(!menuOpen)}
            startIcon={menuOpen ? <X size={20} /> : <Menu size={20} />}
          >
            {locationName}
          </MenuButton>
        </BrandRow>
        <Navigation
          id="primary-navigation"
          aria-label="Navegación principal"
          $open={menuOpen}
        >
          <NavItem to="/" end onClick={handleNavigation}>
            <NavIcon name="dashboard" aria-hidden />
            <span>Inicio</span>
          </NavItem>
          <SectionTitle>Datos y documentos</SectionTitle>
          {dataLinks.map(renderLink)}
          <SectionTitle>Tu restaurante</SectionTitle>
          {restaurantLinks.map(renderLink)}
          <Footer>
            <Button
              variant="tertiary"
              startIcon={<NavIcon name="logout" aria-hidden />}
              onClick={() => setShowLogoutConfirm(true)}
            >
              Cerrar sesión
            </Button>
          </Footer>
        </Navigation>
      </Container>
      <Modal
        isOpen={showLogoutConfirm}
        onClose={() => {
          if (!isLoggingOut) setShowLogoutConfirm(false);
        }}
        title="Cerrar sesión"
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              disabled={isLoggingOut}
              onClick={() => setShowLogoutConfirm(false)}
            >
              Cancelar
            </Button>
            <Button loading={isLoggingOut} onClick={handleLogout}>
              {isLoggingOut ? 'Cerrando sesión…' : 'Cerrar sesión'}
            </Button>
          </>
        }
      >
        Tendrás que iniciar sesión de nuevo para acceder a tu restaurante.
      </Modal>
    </>
  );
};
